import { useState, useEffect, useCallback } from 'react';
import { API } from '../../lib/api';
import { Loader2, Calendar, Users, ChefHat, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/* Vue Cuisine — Menus de Groupe validés (statut = acceptee)
 * Affiche tous les menus groupe acceptés (confirmés clients) pour préparation cuisine.
 */
export default function ChefMenusGroupe() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/menus-groupe-pipeline?statut=acceptee`);
      const data = await r.json();
      setCards(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erreur chargement menus');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return 'Date non précisée';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  const isUpcoming = (iso) => {
    if (!iso) return true;
    try { return new Date(iso) >= new Date(new Date().toDateString()); } catch { return true; }
  };

  const upcomingCards = cards.filter(c => isUpcoming(c.date_event));
  const pastCards = cards.filter(c => !isUpcoming(c.date_event));
  const totalPersonnes = upcomingCards.reduce((s, c) => s + (c.nb_personnes || 0), 0);

  return (
    <div className="space-y-4" data-testid="chef-menus-groupe">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <ChefHat size={20} className="text-[rgb(var(--lm-gold))]" />
            Menus Groupe Validés
          </h1>
          <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mt-0.5">
            Événements confirmés à préparer pour la cuisine
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]" data-testid="refresh-menus-groupe">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-2">
        <div className="lm-card p-3">
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">À venir</p>
          <p className="text-xl font-mono font-bold text-[rgb(var(--lm-text))]">{upcomingCards.length}</p>
        </div>
        <div className="lm-card p-3">
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Couverts à venir</p>
          <p className="text-xl font-mono font-bold text-emerald-400">{totalPersonnes}</p>
        </div>
        <div className="lm-card p-3">
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Historique</p>
          <p className="text-xl font-mono font-bold text-[rgb(var(--lm-text-muted))]">{pastCards.length}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-gold))]" /></div>
      ) : cards.length === 0 ? (
        <div className="lm-card p-10 text-center" data-testid="no-validated-menus">
          <ChefHat size={32} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-2" />
          <p className="text-sm font-medium text-[rgb(var(--lm-text))]">Aucun menu groupe validé pour l'instant</p>
          <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mt-1">
            Les menus passent ici quand le Patron les marque "Acceptée" dans la pipeline.
          </p>
        </div>
      ) : (
        <>
          {/* À VENIR */}
          {upcomingCards.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-[11px] uppercase tracking-wider text-[rgb(var(--lm-gold))] font-bold">À venir</h2>
              <div className="space-y-3">
                {upcomingCards.map(c => <MenuGroupeCard key={c.id} card={c} fmtDate={fmtDate} />)}
              </div>
            </div>
          )}

          {/* HISTORIQUE */}
          {pastCards.length > 0 && (
            <div className="space-y-2 mt-6">
              <h2 className="text-[11px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Historique récent</h2>
              <div className="space-y-3 opacity-60">
                {pastCards.slice(0, 10).map(c => <MenuGroupeCard key={c.id} card={c} fmtDate={fmtDate} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function MenuGroupeCard({ card, fmtDate }) {
  return (
    <div className="lm-card p-4 hover:shadow-lg transition-shadow" data-testid={`menu-groupe-card-${card.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[rgb(var(--lm-text))] truncate">{card.client_nom || card.client_email || 'Client'}</p>
          <p className="text-[11px] text-[rgb(var(--lm-gold))] font-semibold mt-0.5">{card.menu_nom}</p>
        </div>
        <div className="text-right shrink-0 ml-3">
          <div className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text))] font-bold">
            <Users size={11} /> {card.nb_personnes} pers.
          </div>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">{card.menu_prix}€/pers.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-[rgb(var(--lm-text-secondary))] mb-3 pb-3 border-b border-[rgb(var(--lm-border))]/30">
        <Calendar size={12} className="text-[rgb(var(--lm-gold))]" />
        <span className="font-medium capitalize">{fmtDate(card.date_event)}</span>
      </div>

      <div className="space-y-2 text-xs">
        {card.menu_entree && (
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-[rgb(var(--lm-gold))]">Entrée</span>
            <p className="text-[rgb(var(--lm-text))] whitespace-pre-line mt-0.5">{card.menu_entree}</p>
          </div>
        )}
        {card.menu_plat && (
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-[rgb(var(--lm-gold))]">Plat</span>
            <p className="text-[rgb(var(--lm-text))] whitespace-pre-line mt-0.5">{card.menu_plat}</p>
          </div>
        )}
        {card.menu_dessert && (
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-[rgb(var(--lm-gold))]">Dessert</span>
            <p className="text-[rgb(var(--lm-text))] whitespace-pre-line mt-0.5">{card.menu_dessert}</p>
          </div>
        )}
        {card.menu_inclus && (
          <p className="text-[10px] italic text-[rgb(var(--lm-text-muted))] border-t border-[rgb(var(--lm-border))]/20 pt-2 mt-2">
            {card.menu_inclus}
          </p>
        )}
        {card.menu_options?.length > 0 && (
          <div className="border-t border-[rgb(var(--lm-border))]/20 pt-2 mt-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400">Options</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {card.menu_options.map((opt, oi) => (
                <span key={oi} className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300">
                  {opt.nom} {opt.prix ? `+${opt.prix}€` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
        {card.notes && (
          <div className="bg-[rgb(var(--lm-gold))]/5 border-l-2 border-[rgb(var(--lm-gold))] pl-2 py-1.5 mt-2 rounded">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[rgb(var(--lm-gold))]">Notes Patron</p>
            <p className="text-[11px] text-[rgb(var(--lm-text))] italic mt-0.5">{card.notes}</p>
          </div>
        )}
      </div>

      {(card.client_email) && (
        <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-[rgb(var(--lm-border))]/20">
          <FileText size={10} className="text-[rgb(var(--lm-text-muted))]" />
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate">{card.client_email}</p>
        </div>
      )}
    </div>
  );
}
